import { useState, useEffect } from 'react';
import { createTask, updateTask, uploadImage } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, X, Download } from 'lucide-react';
import api from '@/utils/api';

import { TaskBasicTab } from './task-detail/TaskBasicTab';
import { TaskMetalTab } from './task-detail/TaskMetalTab';
import { TaskDiamondsTab } from './task-detail/TaskDiamondsTab';
import { TaskFilesTab } from './task-detail/TaskFilesTab';
import { TaskWorkflowTab } from './task-detail/TaskWorkflowTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyDiamond = { stone_category: 'Diamond', stone_shape: 'Round', stone_size_mm: '', stone_weight_ct: 0, pieces: 1, colour: '', clarity: '', cut: '', setting_type: '', position: '' };
const emptyStone = { stone_name: '', shape: 'Oval', size_mm: '', pieces: 1, weight_ct: 0, setting_type: '' };

const TaskDetailForm = ({ task, onUpdate, onCancel }) => {
  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';
  const isEditMode = !!task;
  const canEdit = !isCustomer;

  const [formData, setFormData] = useState({
    party_cad_no: '', style_no: '', client_name: '', product_name: '', pd_code: '',
    piece_type: 'ring', category: '', collection_name: '', size: '',
    metal_type: '', metal_colour: '', metal_karat: '',
    gross_weight: '', net_weight: '', target_weight: '',
    diamond_type: '', total_diamond_pieces: '', total_diamond_weight_ct: '', diamond_quality_code: '',
    diamonds: [], other_stones: [],
    cad_file_name: '', cad_file_format: '', cad_server_path: '', render_file_name: '',
    product_image: '', additional_images: [],
    stage: '', priority: 'normal', start_date: '', target_date: '',
    quotation_type: '', quotation_file_name: '',
    estimated_metal_cost: '', estimated_diamond_cost: '', estimated_total_cost: '',
    notes: '', special_instructions: '', manufacturing_constraints: '', client_feedback: '',
  });

  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        party_cad_no: task.party_cad_no || '', style_no: task.style_no || '',
        client_name: task.client_name || '', product_name: task.product_name || '',
        pd_code: task.pd_code || '', piece_type: task.piece_type || 'ring',
        category: task.category || '', collection_name: task.collection_name || '',
        size: task.size || '', metal_type: task.metal_type || '',
        metal_colour: task.metal_colour || '', metal_karat: task.metal_karat || '',
        gross_weight: task.gross_weight || '', net_weight: task.net_weight || '',
        target_weight: task.target_weight || '',
        diamond_type: task.diamond_type || '',
        total_diamond_pieces: task.total_diamond_pieces || '',
        total_diamond_weight_ct: task.total_diamond_weight_ct || '',
        diamond_quality_code: task.diamond_quality_code || '',
        diamonds: task.diamonds || [], other_stones: task.other_stones || [],
        cad_file_name: task.cad_file_name || '', cad_file_format: task.cad_file_format || '',
        cad_server_path: task.cad_server_path || '', render_file_name: task.render_file_name || '',
        product_image: task.product_image || '', additional_images: task.additional_images || [],
        stage: task.stage || '', priority: task.priority || 'normal',
        start_date: task.start_date ? new Date(task.start_date).toISOString().split('T')[0] : '',
        target_date: task.target_date ? new Date(task.target_date).toISOString().split('T')[0] : '',
        quotation_type: task.quotation_type || '', quotation_file_name: task.quotation_file_name || '',
        estimated_metal_cost: task.estimated_metal_cost || '',
        estimated_diamond_cost: task.estimated_diamond_cost || '',
        estimated_total_cost: task.estimated_total_cost || '',
        notes: task.notes || '', special_instructions: task.special_instructions || '',
        manufacturing_constraints: task.manufacturing_constraints || '',
        client_feedback: task.client_feedback || '',
      });
      setImageUrl(task.product_image || '');
    }
  }, [task]);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const response = await uploadImage(file);
      const base64Image = response.data.image;
      setFormData(prev => ({ ...prev, product_image: base64Image }));
      setImageUrl(base64Image);
      toast.success('Image uploaded');
    } catch { toast.error('Failed to upload image'); }
    finally { setUploading(false); }
  };

  const handleImageUrl = () => {
    if (imageUrl) { setFormData(prev => ({ ...prev, product_image: imageUrl })); toast.success('Image URL added'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        target_date: formData.target_date ? new Date(formData.target_date).toISOString() : null,
        gross_weight: formData.gross_weight ? parseFloat(formData.gross_weight) : null,
        net_weight: formData.net_weight ? parseFloat(formData.net_weight) : null,
        target_weight: formData.target_weight ? parseFloat(formData.target_weight) : null,
        total_diamond_pieces: formData.total_diamond_pieces ? parseInt(formData.total_diamond_pieces) : null,
        total_diamond_weight_ct: formData.total_diamond_weight_ct ? parseFloat(formData.total_diamond_weight_ct) : null,
        estimated_metal_cost: formData.estimated_metal_cost ? parseFloat(formData.estimated_metal_cost) : null,
        estimated_diamond_cost: formData.estimated_diamond_cost ? parseFloat(formData.estimated_diamond_cost) : null,
        estimated_total_cost: formData.estimated_total_cost ? parseFloat(formData.estimated_total_cost) : null,
      };
      if (isEditMode) { await updateTask(task.id, submitData); toast.success('Task updated'); }
      else { await createTask(submitData); toast.success('Task created'); }
      onUpdate();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save task'); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`${BACKEND_URL}/api/tasks/${task.id}/export?format=excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CAD_${task.internal_cad_no}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exported successfully');
    } catch { toast.error('Failed to export'); }
  };

  return (
    <form onSubmit={handleSubmit} className="h-full flex flex-col">
      <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{isEditMode ? `CAD: ${task.internal_cad_no}` : 'New CAD File'}</h2>
          {isEditMode && <p className="text-sm text-slate-600 mt-1">Party No: {task.party_cad_no} | Created: {new Date(task.created_at).toLocaleDateString()}</p>}
        </div>
        <div className="flex gap-2">
          {isEditMode && <Button type="button" variant="outline" onClick={handleExport} size="sm"><Download size={16} className="mr-2" />Export</Button>}
          <Button type="button" variant="outline" onClick={onCancel} size="sm"><X size={16} className="mr-2" />Cancel</Button>
          {canEdit && <Button type="submit" disabled={saving} size="sm" className="bg-slate-900 hover:bg-slate-800"><Save size={16} className="mr-2" />{saving ? 'Saving...' : 'Save'}</Button>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="metal">Metal</TabsTrigger>
            <TabsTrigger value="diamonds">Diamonds</TabsTrigger>
            <TabsTrigger value="files">Files</TabsTrigger>
            <TabsTrigger value="workflow">Workflow</TabsTrigger>
          </TabsList>
          <TabsContent value="basic"><TaskBasicTab formData={formData} onChange={handleInputChange} canEdit={canEdit} /></TabsContent>
          <TabsContent value="metal"><TaskMetalTab formData={formData} onChange={handleInputChange} canEdit={canEdit} /></TabsContent>
          <TabsContent value="diamonds"><TaskDiamondsTab formData={formData} onChange={handleInputChange} canEdit={canEdit} emptyDiamond={emptyDiamond} emptyStone={emptyStone} /></TabsContent>
          <TabsContent value="files"><TaskFilesTab formData={formData} onChange={handleInputChange} canEdit={canEdit} onImageUpload={handleImageUpload} uploading={uploading} imageUrl={imageUrl} setImageUrl={setImageUrl} onImageUrl={handleImageUrl} /></TabsContent>
          <TabsContent value="workflow"><TaskWorkflowTab formData={formData} onChange={handleInputChange} canEdit={canEdit} /></TabsContent>
        </Tabs>
      </div>
    </form>
  );
};

export default TaskDetailForm;
